/**
 * Attendance Settings page (Salary → Attendance Settings).
 *
 * Add-on for the biometric attendance machine integration. Visible only when
 * the super admin enables `salary_attendance_settings` for the company.
 *
 *  - Rules: shift, grace, late→absent, half day, weekly offs, holidays,
 *    single-punch policy, working-days mode. Saved to
 *    companies/{id}/settings/attendance and consumed by attendanceHelper.js.
 *  - Devices: create a Device Key (Cloud Function `createAttendanceDeviceKey`),
 *    shown ONCE, to paste into the Windows sync agent's config; revoke keys.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { doc, setDoc, collection, getDocs, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  FiSave, FiInfo, FiClock, FiCalendar, FiAlertTriangle, FiCpu, FiPlus,
  FiCopy, FiXCircle, FiCheckCircle, FiX
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import { DEFAULT_ATTENDANCE_SETTINGS, mergeSettings, loadAttendanceSettings, WEEKDAY_NAMES } from '../../utils/attendanceHelper';

const POST_URL = 'https://us-central1-amalerp-29324.cloudfunctions.net/postAttendance';

const AttendanceSettings = () => {
  const { activeCompany, currentUser } = useAuth();
  const { canEdit } = usePagePermissions('salary', 'attendanceSettings');
  const [settings, setSettings] = useState(DEFAULT_ATTENDANCE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [holidayInput, setHolidayInput] = useState('');

  const [devices, setDevices] = useState([]);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [creating, setCreating] = useState(false);
  const [issuedKey, setIssuedKey] = useState(null); // { apiKey, deviceId, deviceName }

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      setSettings(await loadAttendanceSettings(activeCompany.id));
      const snap = await getDocs(query(collection(db, 'companies', activeCompany.id, 'attendanceDevices'), orderBy('createdAt', 'desc')));
      setDevices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally { setLoading(false); }
  }, [activeCompany?.id]);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setSettings(s => ({ ...s, ...patch }));
  const setNested = (key, patch) => setSettings(s => ({ ...s, [key]: { ...s[key], ...patch } }));

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const clean = mergeSettings(settings);
      clean.graceMinutes = Math.max(0, Number(clean.graceMinutes) || 0);
      clean.lateRule.latesPerAbsent = Math.max(1, Number(clean.lateRule.latesPerAbsent) || 3);
      clean.halfDay.minHours = Math.max(0.5, Number(clean.halfDay.minHours) || 4);
      clean.earlyLeave.minutes = Math.max(0, Number(clean.earlyLeave.minutes) || 0);
      clean.overtime.minMinutes = Math.max(0, Number(clean.overtime.minMinutes) || 0);
      clean.overtime.standardHoursPerDay = Math.max(0, Number(clean.overtime.standardHoursPerDay) || 0);
      clean.fixedWorkingDays = Math.min(31, Math.max(1, Number(clean.fixedWorkingDays) || 26));
      clean.holidays = [...new Set(clean.holidays)].sort();
      await setDoc(doc(db, 'companies', activeCompany.id, 'settings', 'attendance'), {
        ...clean,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid || null,
      }, { merge: true });
      setSettings(clean);
      setAlert({ type: 'success', message: 'Attendance rules saved.' });
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally { setSaving(false); }
  };

  const toggleOffDay = (d) => {
    const cur = settings.weeklyOffDays || [];
    set({ weeklyOffDays: cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d].sort() });
  };

  const addHoliday = () => {
    if (!holidayInput) return;
    if (!(settings.holidays || []).includes(holidayInput)) set({ holidays: [...(settings.holidays || []), holidayInput].sort() });
    setHolidayInput('');
  };

  const createDevice = async () => {
    if (!canEdit) return;
    setCreating(true);
    try {
      const fn = httpsCallable(functions, 'createAttendanceDeviceKey');
      const res = await fn({ companyId: activeCompany.id, deviceName: newDeviceName.trim() || 'Attendance Machine' });
      setIssuedKey({ ...res.data, deviceName: newDeviceName.trim() || 'Attendance Machine' });
      setNewDeviceName('');
      await load();
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally { setCreating(false); }
  };

  const revokeDevice = async (dev) => {
    if (!canEdit) return;
    if (!window.confirm(`Revoke key for "${dev.name}"? The agent using it will stop syncing. Logs are kept.`)) return;
    try {
      const fn = httpsCallable(functions, 'revokeAttendanceDevice');
      await fn({ companyId: activeCompany.id, deviceId: dev.id });
      await load();
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    }
  };

  const [clearing, setClearing] = useState(false);
  const clearLogs = async () => {
    if (!canEdit) return;
    if (!window.confirm('Delete ALL synced attendance punches for this company and re-sync from the machine? Manual overrides are kept. Use this after fixing the agent.')) return;
    setClearing(true);
    try {
      const fn = httpsCallable(functions, 'purgeAttendanceLogs');
      const res = await fn({ companyId: activeCompany.id });
      setAlert({ type: 'success', message: `Cleared ${res.data.deleted} punches. On the attendance PC, delete state.json (or wait) so the agent re-syncs corrected times.` });
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally { setClearing(false); }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); setAlert({ type: 'success', message: 'Copied.' }); };

  const fmtTs = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-PK', { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <PageLayout pageTitle="Attendance Settings">
      <Container>
        <Header>
          <Title><FiClock /> Attendance Settings</Title>
          <Subtitle>Rules used to turn machine punches into Present / Late / Half Day / Absent, and the device keys for your attendance machine.</Subtitle>
        </Header>

        {alert && <Alert $type={alert.type}><FiInfo /> {alert.message} <CloseX onClick={() => setAlert(null)}><FiX /></CloseX></Alert>}

        {loading ? <Loading>Loading…</Loading> : (
          <>
            <Section>
              <SectionTitle><FiClock /> Shift &amp; Late Rule</SectionTitle>
              <SectionDesc>Arriving after Shift Start + Grace counts as Late. Late days are still paid; the late rule below converts repeated lates into absents.</SectionDesc>
              <Row $cols={3}>
                <Field><Label>Shift Start</Label><Input type="time" value={settings.shiftStart} onChange={e => set({ shiftStart: e.target.value })} disabled={!canEdit} /></Field>
                <Field><Label>Shift End</Label><Input type="time" value={settings.shiftEnd} onChange={e => set({ shiftEnd: e.target.value })} disabled={!canEdit} /></Field>
                <Field><Label>Grace (minutes)</Label><Input type="number" min="0" value={settings.graceMinutes} onChange={e => set({ graceMinutes: e.target.value })} disabled={!canEdit} /></Field>
              </Row>
              <Toggle>
                <input type="checkbox" checked={!!settings.lateRule.enabled} onChange={e => setNested('lateRule', { enabled: e.target.checked })} disabled={!canEdit} />
                <span>Convert lates into absents</span>
              </Toggle>
              <Row $cols={3}>
                <Field><Label>Every N lates = 1 absent</Label><Input type="number" min="1" value={settings.lateRule.latesPerAbsent} onChange={e => setNested('lateRule', { latesPerAbsent: e.target.value })} disabled={!canEdit || !settings.lateRule.enabled} /></Field>
              </Row>
              <Toggle>
                <input type="checkbox" checked={!!settings.earlyLeave.enabled} onChange={e => setNested('earlyLeave', { enabled: e.target.checked })} disabled={!canEdit} />
                <span>Leaving early also counts as a Late</span>
              </Toggle>
              <Row $cols={3}>
                <Field><Label>Early by more than (minutes)</Label><Input type="number" min="0" value={settings.earlyLeave.minutes} onChange={e => setNested('earlyLeave', { minutes: e.target.value })} disabled={!canEdit || !settings.earlyLeave.enabled} /></Field>
              </Row>
            </Section>

            <Section>
              <SectionTitle><FiAlertTriangle /> Half Day &amp; Missing Punch</SectionTitle>
              <Toggle>
                <input type="checkbox" checked={!!settings.halfDay.enabled} onChange={e => setNested('halfDay', { enabled: e.target.checked })} disabled={!canEdit} />
                <span>Mark Half Day when worked hours are below the minimum</span>
              </Toggle>
              <Row $cols={3}>
                <Field><Label>Minimum hours for full day</Label><Input type="number" min="0.5" step="0.5" value={settings.halfDay.minHours} onChange={e => setNested('halfDay', { minHours: e.target.value })} disabled={!canEdit || !settings.halfDay.enabled} /></Field>
                <Field>
                  <Label>Only one punch in the day (no out)</Label>
                  <SelectBox value={settings.singlePunchPolicy} onChange={e => set({ singlePunchPolicy: e.target.value })} disabled={!canEdit}>
                    <option value="present">Count as Present</option>
                    <option value="halfDay">Count as Half Day</option>
                    <option value="absent">Count as Absent</option>
                  </SelectBox>
                </Field>
              </Row>
              <Hint>Half Day = 0.5 present day in payroll. You can always override any day manually on the Attendance page.</Hint>
            </Section>

            <Section>
              <SectionTitle><FiClock /> Overtime</SectionTitle>
              <SectionDesc>When enabled, time worked past shift end (or past standard hours) becomes OT hours. On Payroll Run, "Pull from Attendance" fills each staff's OT hours and the run's OT rate turns them into an amount.</SectionDesc>
              <Toggle>
                <input type="checkbox" checked={!!settings.overtime.enabled} onChange={e => setNested('overtime', { enabled: e.target.checked })} disabled={!canEdit} />
                <span>Calculate overtime from attendance</span>
              </Toggle>
              <Row $cols={3}>
                <Field>
                  <Label>Count OT</Label>
                  <SelectBox value={settings.overtime.afterShiftEnd === false ? 'standard' : 'shiftEnd'} onChange={e => setNested('overtime', { afterShiftEnd: e.target.value === 'shiftEnd' })} disabled={!canEdit || !settings.overtime.enabled}>
                    <option value="shiftEnd">Time worked after shift end</option>
                    <option value="standard">Time worked beyond standard hours/day</option>
                  </SelectBox>
                </Field>
                <Field><Label>Standard hours / day</Label><Input type="number" min="0" step="0.5" value={settings.overtime.standardHoursPerDay} onChange={e => setNested('overtime', { standardHoursPerDay: e.target.value })} disabled={!canEdit || !settings.overtime.enabled || settings.overtime.afterShiftEnd !== false} /></Field>
                <Field><Label>Ignore OT below (minutes)</Label><Input type="number" min="0" value={settings.overtime.minMinutes} onChange={e => setNested('overtime', { minMinutes: e.target.value })} disabled={!canEdit || !settings.overtime.enabled} /></Field>
              </Row>
              <Hint>The OT rate itself (by salary or fixed per-hour) is set on each Payroll Run, same as before.</Hint>
            </Section>

            <Section>
              <SectionTitle><FiCalendar /> Weekly Off, Holidays &amp; Working Days</SectionTitle>
              <Label>Weekly off days</Label>
              <Chips>
                {WEEKDAY_NAMES.map((n, i) => (
                  <Chip key={n} $on={(settings.weeklyOffDays || []).includes(i)} onClick={() => canEdit && toggleOffDay(i)}>{n}</Chip>
                ))}
              </Chips>
              <Label style={{ marginTop: 14 }}>Public holidays (no attendance expected)</Label>
              <Row $cols={3}>
                <Field><Input type="date" value={holidayInput} onChange={e => setHolidayInput(e.target.value)} disabled={!canEdit} /></Field>
                <Field><SmallBtn onClick={addHoliday} disabled={!canEdit || !holidayInput}><FiPlus /> Add holiday</SmallBtn></Field>
              </Row>
              <Chips>
                {(settings.holidays || []).map(h => (
                  <Chip key={h} $on onClick={() => canEdit && set({ holidays: settings.holidays.filter(x => x !== h) })} title="Click to remove">{h} <FiX size={12} /></Chip>
                ))}
                {(settings.holidays || []).length === 0 && <Hint>No holidays added.</Hint>}
              </Chips>
              <Row $cols={3} style={{ marginTop: 14 }}>
                <Field>
                  <Label>Total working days for payroll</Label>
                  <SelectBox value={settings.workingDaysMode} onChange={e => set({ workingDaysMode: e.target.value })} disabled={!canEdit}>
                    <option value="calendar">Calendar days − offs − holidays</option>
                    <option value="fixed">Fixed number every month</option>
                  </SelectBox>
                </Field>
                <Field><Label>Fixed working days</Label><Input type="number" min="1" max="31" value={settings.fixedWorkingDays} onChange={e => set({ fixedWorkingDays: e.target.value })} disabled={!canEdit || settings.workingDaysMode !== 'fixed'} /></Field>
              </Row>
            </Section>

            <SaveRow>
              <SaveBtn onClick={save} disabled={!canEdit || saving}><FiSave /> {saving ? 'Saving…' : 'Save Rules'}</SaveBtn>
            </SaveRow>

            <Section>
              <SectionTitle><FiCpu /> Attendance Machines (Device Keys)</SectionTitle>
              <SectionDesc>
                Each machine's PC runs the small <b>AmalERP Attendance Agent</b>, which reads new punches and sends them here every few minutes.
                Create one key per machine and paste it into the agent's <code>config.json</code>. The key is shown only once.
              </SectionDesc>
              <Row $cols={3}>
                <Field $span={2}><Input placeholder="Device name (e.g. Head Office – ZKTeco MB20)" value={newDeviceName} onChange={e => setNewDeviceName(e.target.value)} disabled={!canEdit} /></Field>
                <Field><SmallBtn onClick={createDevice} disabled={!canEdit || creating}><FiPlus /> {creating ? 'Creating…' : 'Create Device Key'}</SmallBtn></Field>
              </Row>

              {devices.length === 0 ? <Hint>No devices yet.</Hint> : (
                <DevTable>
                  <thead><tr><th>Device</th><th>Key</th><th>Status</th><th>Last sync</th><th>Last punch</th><th>Total logs</th><th></th></tr></thead>
                  <tbody>
                    {devices.map(d => (
                      <tr key={d.id}>
                        <td>{d.name}</td>
                        <td><code>{d.keyPrefix}</code></td>
                        <td>{d.isActive ? <Ok><FiCheckCircle /> Active</Ok> : <Bad><FiXCircle /> Revoked</Bad>}</td>
                        <td>{fmtTs(d.lastSyncAt)}</td>
                        <td>{fmtTs(d.lastPunchAt)}</td>
                        <td>{d.totalLogs || 0}</td>
                        <td>{d.isActive && canEdit && <LinkBtn onClick={() => revokeDevice(d)}>Revoke</LinkBtn>}</td>
                      </tr>
                    ))}
                  </tbody>
                </DevTable>
              )}
              <Hint style={{ marginTop: 10 }}>Endpoint used by the agent: <code>{POST_URL}</code></Hint>
              <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 12 }}>
                <SmallBtn onClick={clearLogs} disabled={!canEdit || clearing} style={{ color: '#b00020', borderColor: '#b00020' }}>
                  <FiXCircle /> {clearing ? 'Clearing…' : 'Clear all punches & re-sync'}
                </SmallBtn>
                <Hint>Deletes synced punches so the agent re-sends them (e.g. after a timezone fix). Manual overrides are kept.</Hint>
              </div>
            </Section>
          </>
        )}
      </Container>

      {issuedKey && (
        <Overlay onClick={() => setIssuedKey(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle><FiCheckCircle color="#2e7d32" /> Device key created — copy it now</ModalTitle>
            <p style={{ margin: '6px 0 12px', fontSize: 14 }}>This key is shown <b>only once</b>. Paste it into the agent's <code>config.json</code> on the attendance PC.</p>
            <KeyBox>
              <code>{issuedKey.apiKey}</code>
              <SmallBtn onClick={() => copy(issuedKey.apiKey)}><FiCopy /> Copy</SmallBtn>
            </KeyBox>
            <Steps>
              <li>Open <code>C:\AmalERP-Attendance\config.json</code> on the machine's PC.</li>
              <li>Set <code>"apiKey": "{issuedKey.apiKey.slice(0, 16)}…"</code> (full key).</li>
              <li>Run <code>test-connection.bat</code> — it should print <b>OK</b>. The service then syncs automatically.</li>
            </Steps>
            <SaveRow><SaveBtn onClick={() => setIssuedKey(null)}>Done</SaveBtn></SaveRow>
          </Modal>
        </Overlay>
      )}
    </PageLayout>
  );
};

export default AttendanceSettings;

// ─── styles ────────────────────────────────────────────────────────────────
const Container = styled.div`max-width: 960px; margin: 0 auto; padding: 24px; display: flex; flex-direction: column; gap: 16px;`;
const Header = styled.div``;
const Title = styled.h1`margin: 0; font-size: 24px; font-weight: 700; color: ${colors.text.primary}; display: flex; align-items: center; gap: 10px;`;
const Subtitle = styled.div`color: ${colors.text.secondary}; font-size: 14px; margin-top: 4px;`;
const Alert = styled.div`display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; font-size: 14px; position: relative;
  background: ${p => p.$type === 'error' ? '#fdecea' : '#e8f5e9'}; color: ${p => p.$type === 'error' ? '#b71c1c' : '#1b5e20'};`;
const CloseX = styled.span`margin-left: auto; cursor: pointer; display: flex;`;
const Section = styled.div`background: #fff; border: 1px solid #eee; border-radius: 10px; padding: 18px 20px;`;
const SectionTitle = styled.div`display: flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 700; margin-bottom: 6px;`;
const SectionDesc = styled.div`font-size: 13px; color: ${colors.text.secondary}; margin-bottom: 12px; code { background: #f5f5f5; padding: 1px 5px; border-radius: 4px; }`;
const Toggle = styled.label`display: flex; align-items: center; gap: 8px; font-size: 14px; cursor: pointer; margin: 10px 0 4px; input { width: 16px; height: 16px; cursor: pointer; }`;
const Row = styled.div`display: grid; grid-template-columns: repeat(${p => p.$cols || 2}, 1fr); gap: 14px; margin-top: 8px; align-items: end; @media (max-width: 700px) { grid-template-columns: 1fr; }`;
const Field = styled.div`grid-column: span ${p => p.$span || 1};`;
const Label = styled.label`display: block; font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; margin-bottom: 4px;`;
const Input = styled.input`width: 100%; padding: 9px 12px; border: 1px solid #ddd; border-radius: 6px; font-family: inherit; font-size: 14px; &:disabled { opacity: 0.6; }`;
const SelectBox = styled.select`width: 100%; padding: 9px 12px; border: 1px solid #ddd; border-radius: 6px; font-family: inherit; font-size: 14px; background: #fff; &:disabled { opacity: 0.6; }`;
const Hint = styled.div`font-size: 12px; color: ${colors.text.secondary}; margin-top: 6px; code { background: #f5f5f5; padding: 1px 5px; border-radius: 4px; }`;
const Chips = styled.div`display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; align-items: center;`;
const Chip = styled.span`display: inline-flex; align-items: center; gap: 4px; padding: 6px 12px; border-radius: 16px; font-size: 13px; cursor: pointer; user-select: none;
  border: 1px solid ${p => p.$on ? '#800020' : '#ddd'}; background: ${p => p.$on ? '#800020' : '#fff'}; color: ${p => p.$on ? '#fff' : colors.text.primary};`;
const SaveRow = styled.div`display: flex; justify-content: flex-end;`;
const SaveBtn = styled.button`display: inline-flex; align-items: center; gap: 8px; background: #800020; color: #fff; border: none; padding: 12px 20px; border-radius: 6px; font-weight: 600; cursor: pointer; &:disabled { opacity: 0.6; cursor: not-allowed; }`;
const SmallBtn = styled.button`display: inline-flex; align-items: center; gap: 6px; background: #fff; color: #800020; border: 1px solid #800020; padding: 9px 14px; border-radius: 6px; font-weight: 600; font-size: 13px; cursor: pointer; white-space: nowrap; &:disabled { opacity: 0.5; cursor: not-allowed; }`;
const LinkBtn = styled.button`background: none; border: none; color: #b00020; cursor: pointer; font-size: 13px; text-decoration: underline;`;
const Loading = styled.div`padding: 40px; text-align: center; color: ${colors.text.secondary};`;
const DevTable = styled.table`width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 13px;
  th { text-align: left; font-size: 11px; text-transform: uppercase; color: ${colors.text.secondary}; padding: 8px; border-bottom: 1px solid #eee; }
  td { padding: 8px; border-bottom: 1px solid #f3f3f3; } code { font-size: 12px; }`;
const Ok = styled.span`display: inline-flex; align-items: center; gap: 4px; color: #2e7d32;`;
const Bad = styled.span`display: inline-flex; align-items: center; gap: 4px; color: #c62828;`;
const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000;`;
const Modal = styled.div`background: #fff; border-radius: 12px; padding: 22px 24px; width: min(640px, 92vw);`;
const ModalTitle = styled.div`display: flex; align-items: center; gap: 8px; font-size: 18px; font-weight: 700;`;
const KeyBox = styled.div`display: flex; align-items: center; gap: 10px; background: #f7f7f7; border: 1px dashed #bbb; border-radius: 8px; padding: 12px; code { flex: 1; word-break: break-all; font-size: 13px; }`;
const Steps = styled.ol`font-size: 13px; color: ${colors.text.primary}; padding-left: 20px; margin: 14px 0; li { margin: 4px 0; } code { background: #f5f5f5; padding: 1px 5px; border-radius: 4px; }`;
